import { postListingsMatchHealth } from "utils/listingsSearchApi";
import type { ListingsMatchHealthReport } from "utils/listingsSearchApi";

/**
 * The listings-match page watching its own health, and telling the server when it went wrong.
 *
 * Built 2026-11-02 for a realtor whose page kept freezing after last week's fix: the server
 * answered every search in under 2 s, and in six of the freezes the browser's last request was
 * the filters drawer's name facets and then nothing at all for 7–80 minutes. PostHog had no
 * record of the account (blocked in that browser), so nothing showed what the page was doing.
 *
 * What it does, while the page is mounted:
 * - a one-second heartbeat. A VISIBLE page that missed it by more than `FREEZE_MS` froze, and
 *   says so once it runs again (`freeze`, with how long);
 * - every beat and every breadcrumb is kept in localStorage, so a page that never runs again
 *   (hung until the realtor reloaded, crashed, or was killed) is reported by the NEXT listings-
 *   match page this browser opens (`previous_page_died`, with what it was doing last);
 * - long tasks (`PerformanceObserver`), the render rate of the page's main components (a loop
 *   shows as `render_storm`), a search that never answered (`search_stuck`), uncaught errors.
 *
 * Reports go to our own server (`POST /listings-search/client-health`, a log line) and only
 * when something went wrong. Breadcrumbs carry field names, counts and durations — never a
 * contact's name, phone or the text of a search.
 */

export type HealthCrumbData = Record<string, string | number | boolean | null>;

type Crumb = { t: number; n: string; c?: number; d?: HealthCrumbData };
type LongTask = { t: number; ms: number };

type PageRecord = {
  pageId: string;
  startedAt: number;
  lastBeatAt: number;
  hidden: boolean;
  cleanExit: boolean;
  path: string;
  urlLength: number;
  heapMb?: number;
  crumbs: Crumb[];
  longTasks: LongTask[];
};

type Stored = { v: 1; pages: Record<string, PageRecord> };

/**
 * Who is watched: the SHA-256 of the signed-in email (trimmed, lower-cased). Only the realtor
 * this was built for, for now (2026-11-02). A hash because this bundle is public and a
 * customer's address is not ours to publish. Add one with:
 *   node -e 'console.log(require("crypto").createHash("sha256").update("x@y.com").digest("hex"))'
 */
const WATCHED_EMAIL_SHA256 = new Set([
  "cc6e98674f0c15a44fc9353ebaf1e20adfb50871375ac11f68fc4e19b40f1e8f"
]);

export const isListingsMatchHealthWatched = async (
  email: string | null | undefined
): Promise<boolean> => {
  if (!email || !window.crypto?.subtle) return false;
  try {
    const bytes = new TextEncoder().encode(email.trim().toLowerCase());
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    const hex = Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    return WATCHED_EMAIL_SHA256.has(hex);
  } catch {
    return false;
  }
};

const STORAGE_KEY = "webot.listingsMatchHealth.v1";
const BEAT_MS = 1000;
/** A visible page that did not run for this long froze (a beat is due every second). */
const FREEZE_MS = 3000;
/** A record whose page stopped beating this long ago, while visible, belongs to a dead page. */
const DEAD_VISIBLE_MS = 15_000;
/** Chrome throttles a hidden tab's timers to about one a minute, so a hidden page gets longer. */
const DEAD_HIDDEN_MS = 3 * 60_000;
const FORGET_AFTER_MS = 24 * 60 * 60_000;
const STUCK_SEARCH_MS = 30_000;
/** Renders of the context provider in one second, three seconds running, is a loop. */
const RENDER_STORM_PER_BEAT = 40;
const MAX_CRUMBS = 100;
const MAX_LONG_TASKS = 50;
const MAX_REPORTS_PER_PAGE = 6;
const MAX_ERROR_REPORTS_PER_PAGE = 2;

type Monitor = {
  record: PageRecord;
  timer: ReturnType<typeof setInterval>;
  renders: Record<string, number>;
  stormBeats: number;
  stormReported: boolean;
  pendingSearches: Map<number, { startedAt: number; stuckReported: boolean }>;
  nextSearchId: number;
  reportsSent: number;
  errorReportsSent: number;
  lastPersistAt: number;
  cleanup: Array<() => void>;
};

let monitor: Monitor | null = null;

const readStored = (): Stored => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Stored) : null;
    if (parsed && parsed.v === 1 && parsed.pages && typeof parsed.pages === "object") return parsed;
  } catch {
    // Unreadable storage is an empty one.
  }
  return { v: 1, pages: {} };
};

const writeStored = (stored: Stored): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage full or blocked: the live reports still work.
  }
};

const persist = (force = false): void => {
  if (!monitor) return;
  const now = Date.now();
  // A burst of breadcrumbs (a loop) must not turn into a burst of storage writes; the beat
  // writes what was skipped.
  if (!force && now - monitor.lastPersistAt < 50) return;
  monitor.lastPersistAt = now;
  const stored = readStored();
  stored.pages[monitor.record.pageId] = monitor.record;
  writeStored(stored);
};

const forgetRecord = (pageId: string): void => {
  const stored = readStored();
  delete stored.pages[pageId];
  writeStored(stored);
};

const heapMb = (): number | undefined => {
  const memory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
  return memory ? Math.round(memory.usedJSHeapSize / 1_000_000) : undefined;
};

const deviceInfo = (): HealthCrumbData => {
  const memory = (performance as unknown as { memory?: { jsHeapSizeLimit: number } }).memory;
  const nav = navigator as unknown as { deviceMemory?: number; hardwareConcurrency?: number };
  return {
    cores: nav.hardwareConcurrency ?? null,
    memoryGb: nav.deviceMemory ?? null,
    heapMb: heapMb() ?? null,
    heapLimitMb: memory ? Math.round(memory.jsHeapSizeLimit / 1_000_000) : null,
    viewport: `${window.innerWidth}x${window.innerHeight}`
  };
};

const pushCrumb = (record: PageRecord, name: string, data?: HealthCrumbData): boolean => {
  const now = Date.now();
  const last = record.crumbs[record.crumbs.length - 1];
  // The same thing again (a draft field typed into, a render count) collapses into one crumb
  // with a count, so a busy second does not push the useful history out.
  if (last && last.n === name && JSON.stringify(last.d ?? null) === JSON.stringify(data ?? null)) {
    last.c = (last.c ?? 1) + 1;
    last.t = now;
    return false;
  }
  record.crumbs.push(data ? { t: now, n: name, d: data } : { t: now, n: name });
  if (record.crumbs.length > MAX_CRUMBS) record.crumbs.splice(0, record.crumbs.length - MAX_CRUMBS);
  return true;
};

const send = (
  kind: ListingsMatchHealthReport["kind"],
  source: PageRecord,
  extra: Partial<ListingsMatchHealthReport> = {}
): Promise<boolean> => {
  const report: ListingsMatchHealthReport = {
    kind,
    pageId: source.pageId,
    pageStartedAt: source.startedAt,
    reportedAt: Date.now(),
    path: source.path,
    urlLength: source.urlLength,
    device: deviceInfo(),
    breadcrumbs: source.crumbs.slice(-MAX_CRUMBS),
    longTasks: source.longTasks.slice(-MAX_LONG_TASKS),
    ...extra
  };
  // Never throws, synchronously or not: telemetry must not become the page's next error.
  return Promise.resolve()
    .then(() => postListingsMatchHealth(report))
    .catch(() => false);
};

const report = (
  kind: ListingsMatchHealthReport["kind"],
  extra?: Partial<ListingsMatchHealthReport>
): void => {
  if (!monitor || monitor.reportsSent >= MAX_REPORTS_PER_PAGE) return;
  monitor.reportsSent += 1;
  void send(kind, monitor.record, extra);
};

/** Pages this browser left running that stopped beating without unloading: report, then forget. */
const reportDeadPages = (currentPageId: string): void => {
  const stored = readStored();
  const now = Date.now();
  for (const record of Object.values(stored.pages)) {
    if (!record || record.pageId === currentPageId) continue;
    const silentFor = now - (record.lastBeatAt ?? 0);
    if (record.cleanExit || silentFor > FORGET_AFTER_MS) {
      forgetRecord(record.pageId);
      continue;
    }
    const deadAfter = record.hidden ? DEAD_HIDDEN_MS : DEAD_VISIBLE_MS;
    if (silentFor < deadAfter) continue; // another tab, still alive
    void send("previous_page_died", record, {
      lastBeatAt: record.lastBeatAt,
      gapMs: silentFor,
      hiddenAtLastBeat: record.hidden,
      wasDiscarded: Boolean((document as unknown as { wasDiscarded?: boolean }).wasDiscarded),
      device: { ...deviceInfo(), lastHeapMb: record.heapMb ?? null }
    }).then((ok) => {
      // Kept on a failed send, so the next page tries again.
      if (ok) forgetRecord(record.pageId);
    });
  }
};

const beat = (): void => {
  if (!monitor) return;
  const { record } = monitor;
  const now = Date.now();
  const visible = document.visibilityState === "visible";
  const gap = now - record.lastBeatAt;

  if (visible && !record.hidden && gap > FREEZE_MS) {
    pushCrumb(record, "frozen", { ms: gap });
    report("freeze", { gapMs: gap, lastBeatAt: record.lastBeatAt });
  }

  const renders = monitor.renders;
  const renderTotal = Object.values(renders).reduce((sum, n) => sum + n, 0);
  if (renderTotal > 0) {
    const last = record.crumbs[record.crumbs.length - 1];
    if (last && last.n === "renders" && last.d) {
      // Summed into the running crumb: one crumb per stretch of rendering, not one per second.
      for (const [name, n] of Object.entries(renders)) {
        last.d[name] = (Number(last.d[name]) || 0) + n;
      }
      last.c = (last.c ?? 1) + 1;
      last.t = now;
    } else {
      pushCrumb(record, "renders", { ...renders });
    }
  }
  const providerRenders = renders.provider ?? 0;
  monitor.stormBeats = providerRenders > RENDER_STORM_PER_BEAT ? monitor.stormBeats + 1 : 0;
  if (monitor.stormBeats >= 3 && !monitor.stormReported) {
    monitor.stormReported = true;
    report("render_storm");
  }
  monitor.renders = {};

  monitor.pendingSearches.forEach((search) => {
    if (!search.stuckReported && now - search.startedAt > STUCK_SEARCH_MS) {
      search.stuckReported = true;
      pushCrumb(record, "search_still_pending", { ms: now - search.startedAt });
      report("search_stuck", { gapMs: now - search.startedAt });
    }
  });

  record.lastBeatAt = now;
  record.hidden = !visible;
  const previousUrlLength = record.urlLength;
  record.urlLength = window.location.href.length;
  // The page keeps its whole search, the picked contact included, in the address. Firebase
  // Hosting answers a reload of an address over ~8,192 bytes with a bare 414 page. (The
  // starting length is on `page_start`; this notes each change while it is that long.)
  if (record.urlLength > 7000 && record.urlLength !== previousUrlLength) {
    pushCrumb(record, "url_long", { length: record.urlLength });
  }
  record.heapMb = heapMb();
  persist(true);
};

/** Starts watching the page. Returns the stop function (the page's unmount). */
export const startListingsMatchHealth = (): (() => void) => {
  if (monitor) stopListingsMatchHealth();
  const now = Date.now();
  const pageId = `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const record: PageRecord = {
    pageId,
    startedAt: now,
    lastBeatAt: now,
    hidden: document.visibilityState !== "visible",
    cleanExit: false,
    path: window.location.pathname,
    urlLength: window.location.href.length,
    heapMb: heapMb(),
    crumbs: [],
    longTasks: []
  };
  monitor = {
    record,
    timer: setInterval(beat, BEAT_MS),
    renders: {},
    stormBeats: 0,
    stormReported: false,
    pendingSearches: new Map(),
    nextSearchId: 1,
    reportsSent: 0,
    errorReportsSent: 0,
    lastPersistAt: 0,
    cleanup: []
  };
  const params = new URLSearchParams(window.location.search);
  pushCrumb(record, "page_start", {
    urlLength: record.urlLength,
    contactInUrl: params.has("contact"),
    contactNumberInUrl: params.has("cn"),
    navigation:
      (performance.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined)
        ?.type ?? null
  });
  persist(true);

  const onPageHide = () => {
    if (!monitor) return;
    monitor.record.cleanExit = true;
    persist(true);
  };
  const onPageShow = (event: PageTransitionEvent) => {
    if (!monitor || !event.persisted) return;
    monitor.record.cleanExit = false;
    monitor.record.lastBeatAt = Date.now();
    persist(true);
  };
  const onVisibility = () => {
    if (!monitor) return;
    const visible = document.visibilityState === "visible";
    pushCrumb(monitor.record, visible ? "visible" : "hidden");
    // Coming back is not a freeze: the gap belongs to the tab having been in the background.
    if (visible) monitor.record.lastBeatAt = Date.now();
    monitor.record.hidden = !visible;
    persist(true);
  };
  const onError = (event: ErrorEvent) => {
    noteHealthError(event.message || String(event.error));
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    noteHealthError(reason instanceof Error ? reason.message : String(reason));
  };
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  monitor.cleanup.push(() => {
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  });

  try {
    const observer = new PerformanceObserver((list) => {
      if (!monitor) return;
      for (const entry of list.getEntries()) {
        if (entry.duration < 200) continue;
        const task = {
          t: Math.round(performance.timeOrigin + entry.startTime),
          ms: Math.round(entry.duration)
        };
        monitor.record.longTasks.push(task);
        if (monitor.record.longTasks.length > MAX_LONG_TASKS) monitor.record.longTasks.shift();
        if (task.ms >= 1000) pushCrumb(monitor.record, "long_task", { ms: task.ms });
      }
    });
    observer.observe({ type: "longtask", buffered: true });
    monitor.cleanup.push(() => observer.disconnect());
  } catch {
    // No long-task API in this browser: the heartbeat still catches a freeze.
  }

  // Later, not now: the auth token the report needs is the page's first priority too.
  const deadPagesTimer = setTimeout(() => reportDeadPages(pageId), 5000);
  monitor.cleanup.push(() => clearTimeout(deadPagesTimer));

  return stopListingsMatchHealth;
};

export const stopListingsMatchHealth = (): void => {
  if (!monitor) return;
  const { record } = monitor;
  clearInterval(monitor.timer);
  monitor.cleanup.forEach((fn) => fn());
  monitor = null;
  // Left the page inside the app: a clean exit, nothing to report later.
  forgetRecord(record.pageId);
};

/** One step of what the page was doing. A no-op unless the page is being watched. */
export const healthCrumb = (name: string, data?: HealthCrumbData): void => {
  if (!monitor) return;
  // A new crumb is written at once (the step right before a hang is the one that matters); a
  // repeat of the last one waits for the beat when it comes in a burst.
  persist(pushCrumb(monitor.record, name, data));
};

/** Counted, never stored one by one: the beat turns the counts into one crumb a stretch. */
export const noteHealthRender = (component: string): void => {
  if (!monitor) return;
  monitor.renders[component] = (monitor.renders[component] ?? 0) + 1;
};

/** Browser noise every page throws; kept as a crumb, never spent on a report. */
const NOISE_ERRORS = [/ResizeObserver loop/i, /^Script error\.?$/i];

export const noteHealthError = (message: string): void => {
  if (!monitor) return;
  const text = (message || "unknown").slice(0, 300);
  pushCrumb(monitor.record, "error", { message: text });
  persist(true);
  if (NOISE_ERRORS.some((pattern) => pattern.test(text))) return;
  if (monitor.errorReportsSent >= MAX_ERROR_REPORTS_PER_PAGE) return;
  monitor.errorReportsSent += 1;
  report("error", { error: text });
};

/** A search left for the server. Pass the id back to `healthSearchEnded`. */
export const healthSearchStarted = (data?: HealthCrumbData): number => {
  if (!monitor) return 0;
  const id = monitor.nextSearchId;
  monitor.nextSearchId += 1;
  monitor.pendingSearches.set(id, { startedAt: Date.now(), stuckReported: false });
  healthCrumb("search_start", { id, ...data });
  return id;
};

export const healthSearchEnded = (id: number, data?: HealthCrumbData): void => {
  if (!monitor || !id) return;
  const search = monitor.pendingSearches.get(id);
  monitor.pendingSearches.delete(id);
  healthCrumb("search_end", {
    id,
    ms: search ? Date.now() - search.startedAt : null,
    ...data
  });
};
